import { useEffect, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy } from '@/i18n/ns/overlays';
import companySettingsCopy from '@/i18n/ns/company-settings';
import { useOptionalWorkspace } from '@/workspace/context';
import { useCompany } from './context';
import { TeamConfirmDialog, type TeamConfirmation } from './team-confirm';
import { ChoiceMenu, TeamInvitation, TeamMember } from './team-member';
import './team.css';
import {
  changeMember,
  createTeamInvite,
  fetchTeam,
  removeMember,
  revokeTeamInvite,
  transferOwnership,
  type TeamData,
} from './team-api';

export function CompanyTeam() {
  const { data, adopt, refresh } = useCompany();
  const [team, setTeam] = useState<TeamData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteRequest, setInviteRequest] = useState<{
    email: string;
    role: string;
    operationKey: string;
    expiresAt: string;
  } | null>(null);
  const [confirmation, setConfirmation] = useState<TeamConfirmation | null>(null);
  const ws = useOptionalWorkspace();
  const overlays = useNamespaceCopy(overlaysCopy);
  const copy = useNamespaceCopy(companySettingsCopy);
  const owner = data?.membership?.role === 'owner';
  // Collaboration v1: a company admin invites members too (never admins).
  const inviter = owner || data?.membership?.permissions?.inviteMembers === true;
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.retry);
    } finally {
      setBusy(false);
    }
  };
  const load = () => run(async () => setTeam(await fetchTeam()));
  // Runs the act the person confirmed; the dialog closes only once it is done.
  const confirmed = (asked: TeamConfirmation) =>
    run(async () => {
      if (asked.action === 'revoke') {
        setTeam(await revokeTeamInvite(asked.invitation.id));
        setInviteUrl(null);
        setInviteRequest(null);
        const email = asked.invitation.email;
        ws?.notify(formatCopy(overlays.invitationRevoked, { email }), null, 'success');
      } else if (asked.action === 'remove') setTeam(await removeMember(asked.person.userId));
      else adopt(await transferOwnership(asked.person.userId));
      setConfirmation(null);
    });
  useEffect(() => {
    void load();
  }, [data?.membership?.role]);
  const invite = () =>
    run(async () => {
      const request =
        inviteRequest?.email === email && inviteRequest.role === role
          ? inviteRequest
          : {
              email,
              role,
              operationKey: crypto.randomUUID(),
              expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
            };
      setInviteRequest(request);
      const result = await createTeamInvite(request);
      setInviteRequest(null);
      setInviteUrl(result.url);
      setTeam(await fetchTeam());
    });
  return (
    <section className="company-team" aria-label={copy.team}>
      <header className="company-team-heading">
        <h1>{copy.team}</h1>
        <p>{copy.teamDescription}</p>
      </header>
      {error && (
        <p role="alert" className="company-error">
          {error}
        </p>
      )}
      {!team ? (
        <button disabled={busy} onClick={() => void load()}>
          {copy.loadTeam}
        </button>
      ) : (
        <>
          <section className="company-team-section">
            <div className="company-team-section-heading">
              <h2>
                {copy.people} <span>{team.people.length}</span>
              </h2>
            </div>
            <div className="company-team-columns" aria-hidden="true">
              <span>{copy.member}</span>
              <span>{copy.role}</span>
              <span>{copy.companyPublishing}</span>
              <span />
            </div>
            {team.people.map((person) => (
              <TeamMember
                key={person.userId}
                person={person}
                owner={owner}
                busy={busy}
                onSave={(draft) =>
                  void run(async () => {
                    setTeam(await changeMember(draft));
                    await refresh();
                  })
                }
                onRemove={() => setConfirmation({ person, action: 'remove' })}
                onTransfer={() => setConfirmation({ person, action: 'transfer' })}
              />
            ))}
            <p className="company-team-privacy">{copy.teamPrivacy}</p>
          </section>
          {inviter && (
            <section className="company-team-section company-team-invite">
              <div className="company-team-section-heading">
                <h2>{copy.inviteColleague}</h2>
              </div>
              <p>{copy.inviteIntro}</p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void invite();
                }}
              >
                <label>
                  {copy.emailAddress}
                  <input
                    type="email"
                    required
                    placeholder={copy.invitationPlaceholder}
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setInviteUrl(null);
                    }}
                  />
                </label>
                {owner && (
                  <label>
                    {copy.role}
                    <ChoiceMenu
                      label={copy.role}
                      title={copy.role}
                      value={role}
                      options={[
                        { value: 'member', label: copy.member },
                        { value: 'admin', label: copy.admin },
                      ]}
                      onChange={setRole}
                    />
                  </label>
                )}
                <button className="company-team-primary" disabled={busy}>
                  {copy.createInvitation}
                </button>
              </form>
              <p>{copy.invitationShare}</p>
              {inviteUrl && (
                <label className="company-team-invite-link">
                  {copy.invitationLink}
                  <input
                    aria-label={copy.invitationLink}
                    readOnly
                    value={inviteUrl}
                    onFocus={(e) => e.target.select()}
                  />
                </label>
              )}
              {team.invitations.map((invitation) => (
                <TeamInvitation
                  key={invitation.id}
                  invitation={invitation}
                  busy={busy}
                  onRevoke={() => setConfirmation({ invitation, action: 'revoke' })}
                />
              ))}
            </section>
          )}
        </>
      )}
      {confirmation && (
        <TeamConfirmDialog
          confirmation={confirmation}
          busy={busy}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => void confirmed(confirmation)}
        />
      )}
    </section>
  );
}
